import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/admin';

function useAdminApi(secret) {
  return useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'x-admin-secret': secret, ...(opts.headers || {}) },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Fehler');
    return data;
  }, [secret]);
}

const TIER_OPTIONS = [
  { value: 'basic',  label: 'Basic',  users: 1  },
  { value: 'pro',    label: 'Pro',    users: 3  },
  { value: 'team',   label: 'Team',   users: 5  },
  { value: 'agency', label: 'Agency', users: 20 },
];
const TIER_COLORS = { basic: '#00e5ff', pro: '#9944ff', team: '#00ff88', agency: '#ffaa00' };

// ── Firmenprofil-Modal ────────────────────────────────────────────────────────
function CompanyProfileModal({ userId, userName, adminApi, onClose, onMsg }) {
  const [profile, setProfile]   = useState(null);
  const [text,    setText]      = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    adminApi(`/users/${userId}/company-profile`)
      .then(d => { setProfile(d); setText(d?.profile_text || ''); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  async function handleSave() {
    setSaving(true);
    try { await adminApi(`/users/${userId}/company-profile`, { method: 'POST', body: JSON.stringify({ profile_text: text }) }); onMsg('Firmenprofil gespeichert.'); onClose(); }
    catch (e) { onMsg('Fehler: ' + e.message); } finally { setSaving(false); }
  }
  async function handleReset() {
    if (!window.confirm('KI-Profil zurücksetzen?')) return;
    setResetting(true);
    try { const d = await adminApi(`/users/${userId}/reset-company-profile`, { method: 'POST' }); onMsg(d.message); setText(''); setProfile(p => p ? { ...p, profile_text: null, generated_at: null } : p); }
    catch (e) { onMsg('Fehler: ' + e.message); } finally { setResetting(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: '100%', maxWidth: 640, background: '#0a0a1f', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 16, padding: 28, maxHeight: '90dvh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div><div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Firmenprofil</div><div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{userName}</div></div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        {loading ? <div style={{ height: 100, background: 'rgba(255,255,255,0.05)', borderRadius: 8 }} /> : !profile ? <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Kein Firmenprofil angelegt.</p> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[['Firma', profile.company_name],['Branche', profile.industry],['Zielgruppe', profile.target_audience],['Standort', profile.location]].map(([l,v]) => v ? <div key={l} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}><div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{l}</div><div style={{ fontSize: 12, color: '#fff' }}>{v}</div></div> : null)}
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em' }}>KI-PROFIL {profile.generated_at && <span style={{ color: '#00ff88' }}>· {new Date(profile.generated_at).toLocaleDateString('de-DE')}</span>}</label>
                {profile.profile_text && <button onClick={handleReset} disabled={resetting} style={{ fontSize: 11, padding: '3px 10px', background: 'rgba(255,170,0,0.1)', border: '1px solid rgba(255,170,0,0.3)', borderRadius: 6, color: '#ffaa00', cursor: 'pointer' }}>{resetting ? '...' : '↺ Zurücksetzen'}</button>}
              </div>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={8} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 9, color: '#fff', fontSize: 13, lineHeight: 1.7, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{ padding: '9px 20px', background: 'linear-gradient(135deg,#00e5ff,#0077ff)', border: 'none', borderRadius: 9, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{saving ? 'Speichere...' : '▶ Speichern'}</button>
              <button onClick={onClose} style={{ padding: '9px 16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 9, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13 }}>Abbrechen</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Limit-Modal ───────────────────────────────────────────────────────────────
function LimitModal({ user, adminApi, onClose, onMsg, onReload }) {
  const [posts,      setPosts]      = useState(user.custom_weekly_posts     ?? '');
  const [publishes,  setPublishes]  = useState(user.custom_weekly_publishes ?? '');
  const [images,     setImages]     = useState(user.custom_weekly_images    ?? '');
  const [usage,      setUsage]      = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [resetting,  setResetting]  = useState(false);
  const [tier,       setTier]       = useState(user.tier || 'basic');
  const [tierSaving, setTierSaving] = useState(false);

  const tierDefaults = { basic: [5,10,5], pro: [15,30,15], team: [40,80,40], agency: [200,400,200] };
  const defaults = tierDefaults[tier] || [10,20,10];

  useEffect(() => {
    loadUsage();
  }, [user.id]);

  async function loadUsage() {
    try { const d = await adminApi(`/usage/${user.id}`); setUsage(d); } catch {}
  }

  async function handleSave() {
    setSaving(true);
    try {
      await adminApi(`/users/${user.id}/limits`, { method: 'POST', body: JSON.stringify({ weekly_posts: posts, weekly_publishes: publishes, weekly_images: images }) });
      onMsg(`Limits für ${user.name || user.email} gespeichert.`);
      if (onReload) onReload();
      onClose();
    } catch (e) { onMsg('Fehler: ' + e.message); } finally { setSaving(false); }
  }

  // Reset: löscht Nutzungs-Tracking dieser Woche in der DB
  async function handleResetUsage() {
    if (!window.confirm(`Wochennutzung von ${user.name || user.email} zurücksetzen?`)) return;
    setResetting(true);
    try {
      await adminApi(`/users/${user.id}/reset-usage`, { method: 'POST', body: JSON.stringify({}) });
      onMsg('Nutzung zurückgesetzt.');
      await loadUsage();
    } catch (e) { onMsg('Fehler: ' + e.message); } finally { setResetting(false); }
  }

  async function handleTierChange(newTier) {
    if (newTier === user.tier) return;
    setTierSaving(true);
    try {
      await adminApi(`/users/${user.id}/tier`, { method: 'POST', body: JSON.stringify({ tier: newTier }) });
      setTier(newTier);
      onMsg(`Tier für ${user.name || user.email} auf ${newTier} geändert.`);
      if (onReload) onReload();
    } catch (e) { onMsg('Fehler: ' + e.message); } finally { setTierSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: '100%', maxWidth: 520, background: '#0a0a1f', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 16, padding: 28, maxHeight: '90dvh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Konto verwalten</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{user.name || user.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        {/* ── Tier-Wechsel ── */}
        <div style={{ marginBottom: 20, padding: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginBottom: 10 }}>TARIF ÄNDERN</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TIER_OPTIONS.map(t => {
              const isActive = tier === t.value;
              const c = TIER_COLORS[t.value];
              return (
                <button key={t.value} onClick={() => handleTierChange(t.value)} disabled={tierSaving}
                  style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 400, transition: 'all 0.15s', background: isActive ? `${c}20` : 'transparent', color: isActive ? c : 'rgba(255,255,255,0.45)', border: `1px solid ${isActive ? c + '55' : 'rgba(255,255,255,0.12)'}` }}>
                  {isActive && '✓ '}{t.label}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
            {tierDefaults[tier] ? `${tier}: ${tierDefaults[tier][0]} Posts · ${tierDefaults[tier][1]} Publishes · ${tierDefaults[tier][2]} Bilder pro Woche` : ''}
          </div>
        </div>

        {/* ── Aktuelle Nutzung ── */}
        {usage && (
          <div style={{ marginBottom: 20, padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>DIESE WOCHE GENUTZT ({usage.week_start})</div>
              <button onClick={handleResetUsage} disabled={resetting}
                style={{ fontSize: 10, padding: '3px 10px', background: 'rgba(255,0,85,0.1)', border: '1px solid rgba(255,0,85,0.3)', borderRadius: 6, color: '#ff4444', cursor: 'pointer' }}>
                {resetting ? '...' : '↺ Nutzung zurücksetzen'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[['Posts', usage.posts],['Publishes', usage.publishes],['Bilder', usage.images]].map(([l, u]) => (
                <div key={l} style={{ textAlign: 'center', padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: u.used >= u.limit ? '#ff4444' : '#00e5ff' }}>{u.used}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>/ {u.limit >= 999 ? '∞' : u.limit} {l}</div>
                  {u.limit < 999 && (
                    <div style={{ height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginTop: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min((u.used/u.limit)*100, 100)}%`, background: u.used >= u.limit ? '#ff4444' : '#00e5ff', borderRadius: 2 }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Custom Limits ── */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', marginBottom: 10 }}>INDIVIDUELLE LIMITS (leer = Tier-Standard)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              ['Posts/Woche', posts, setPosts, defaults[0]],
              ['Publishes/Woche', publishes, setPublishes, defaults[1]],
              ['Bilder/Woche', images, setImages, defaults[2]],
            ].map(([label, val, setter, def]) => (
              <label key={label} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.06em' }}>{label.toUpperCase()}</span>
                <input type="number" min={0} max={9999} value={val} onChange={e => setter(e.target.value)}
                  placeholder={String(def)}
                  style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 14, textAlign: 'center' }} />
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>Standard: {def}</span>
              </label>
            ))}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '11px', background: 'linear-gradient(135deg,#00e5ff,#0077ff)', border: 'none', borderRadius: 9, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          {saving ? 'Speichere...' : '▶ Limits speichern'}
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [secret,    setSecret]    = useState(() => sessionStorage.getItem('admin_secret') || '');
  const [authed,    setAuthed]    = useState(false);
  const [authError, setAuthError] = useState('');
  const [tab,       setTab]       = useState('users');
  const [users,     setUsers]     = useState([]);
  const [invites,   setInvites]   = useState([]);
  const [tierLimits, setTierLimits] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [msg,       setMsg]       = useState('');
  const [editUser,  setEditUser]  = useState(null);
  const [limitUser, setLimitUser] = useState(null);

  const [invTier,    setInvTier]    = useState('basic');
  const [invLabel,   setInvLabel]   = useState('');
  const [invMaxUses, setInvMaxUses] = useState(1);
  const [newInvite,  setNewInvite]  = useState(null);

  const [changelog,  setChangelog]  = useState([]);
  const [clTitle,    setClTitle]    = useState('');
  const [clDesc,     setClDesc]     = useState('');
  const [clCat,      setClCat]      = useState('feature');
  const [clSaving,   setClSaving]   = useState(false);

  const adminApi = useAdminApi(secret);
  function showMsg(m) { setMsg(m); setTimeout(() => setMsg(''), 5000); }

  async function handleLogin(e) {
    e.preventDefault();
    try { await adminApi('/users'); sessionStorage.setItem('admin_secret', secret); setAuthed(true); setAuthError(''); }
    catch { setAuthError('Falsches Admin-Passwort.'); }
  }

  async function loadData() {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([adminApi('/users'), adminApi('/invites')]);
      setUsers(u); setInvites(i);
      adminApi('/tier-limits').then(setTierLimits).catch(() => {});
      adminApi('/changelog').then(setChangelog).catch(() => {});
    } catch (e) { showMsg('Fehler: ' + e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { if (authed) loadData(); }, [authed]);

  async function toggleActive(user) {
    const action = user.active === false ? 'activate' : 'deactivate';
    const reason = action === 'deactivate' ? (window.prompt('Grund (optional):', 'Abo beendet') ?? 'Abo beendet') : undefined;
    if (reason === null) return;
    try { await adminApi(`/users/${user.id}/${action}`, { method: 'POST', body: JSON.stringify({ reason }) }); showMsg(`${user.email} ${action === 'activate' ? 'aktiviert ✓' : 'deaktiviert ✕'}`); loadData(); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  async function triggerWeekly(userId) {
    try { const d = await adminApi('/weekly-suggestions/trigger', { method: 'POST', body: JSON.stringify({ user_id: userId }) }); showMsg(d.message); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  async function generateInvite() {
    try { const d = await adminApi('/invites/generate', { method: 'POST', body: JSON.stringify({ tier: invTier, max_uses: invMaxUses, label: invLabel || undefined }) }); setNewInvite(d); loadData(); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  async function deleteInvite(code) {
    if (!window.confirm(`Code ${code} löschen?`)) return;
    try { await adminApi(`/invites/${code}`, { method: 'DELETE' }); loadData(); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  async function addChangelog() {
    if (!clTitle.trim()) return showMsg('Titel ist erforderlich.');
    setClSaving(true);
    try {
      await adminApi('/changelog', { method: 'POST', body: JSON.stringify({ title: clTitle, description: clDesc || undefined, category: clCat }) });
      setClTitle(''); setClDesc(''); setClCat('feature');
      adminApi('/changelog').then(setChangelog).catch(() => {});
      showMsg('Changelog-Eintrag gespeichert.');
    } catch (e) { showMsg('Fehler: ' + e.message); } finally { setClSaving(false); }
  }

  async function deleteChangelog(id) {
    if (!window.confirm('Eintrag löschen?')) return;
    try { await adminApi(`/changelog/${id}`, { method: 'DELETE' }); adminApi('/changelog').then(setChangelog).catch(() => {}); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  async function saveTierLimit(tier, posts, publishes, images) {
    try { await adminApi('/tier-limits', { method: 'POST', body: JSON.stringify({ tier, weekly_posts: posts, weekly_publishes: publishes, weekly_images: images }) }); showMsg(`Tier ${tier} gespeichert.`); loadData(); }
    catch (e) { showMsg('Fehler: ' + e.message); }
  }

  if (!authed) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050510' }}>
        <div style={{ width: 360, padding: 32, background: 'rgba(10,10,30,0.95)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 16 }}>
          <div style={{ fontFamily: 'Arial Black', fontSize: 20, color: '#fff', marginBottom: 4 }}>look<span style={{ color: '#3B8BD4' }}>4</span>more</div>
          <div style={{ fontSize: 11, color: '#00e5ff', letterSpacing: '0.1em', marginBottom: 24 }}>ADMIN DASHBOARD</div>
          <form onSubmit={handleLogin}>
            <input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Admin-Passwort" autoFocus
              style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 9, color: '#fff', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', outline: 'none' }} />
            {authError && <p style={{ color: '#ff4444', fontSize: 12, marginBottom: 10 }}>{authError}</p>}
            <button type="submit" style={{ width: '100%', padding: '11px', background: 'linear-gradient(135deg, #00e5ff, #0077ff)', border: 'none', borderRadius: 9, color: '#000', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Einloggen</button>
          </form>
        </div>
      </div>
    );
  }

  const totalUsers  = users.length;
  const activeUsers = users.filter(u => u.active !== false).length;
  const availInvites = invites.filter(i => i.current_uses < i.max_uses).length;

  return (
    <div style={{ minHeight: '100dvh', background: '#050510', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
      {editUser  && <CompanyProfileModal userId={editUser.id} userName={editUser.name || editUser.email} adminApi={adminApi} onClose={() => setEditUser(null)} onMsg={showMsg} />}
      {limitUser && <LimitModal user={limitUser} adminApi={adminApi} onClose={() => { setLimitUser(null); loadData(); }} onMsg={showMsg} onReload={loadData} />}

      {/* Header */}
      <div style={{ background: 'rgba(10,10,25,0.95)', borderBottom: '1px solid rgba(0,229,255,0.15)', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontFamily: 'Arial Black', fontSize: 17, color: '#fff' }}>look<span style={{ color: '#3B8BD4' }}>4</span>more</span>
          <span style={{ fontSize: 11, color: '#00e5ff', letterSpacing: '0.12em', background: 'rgba(0,229,255,0.08)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(0,229,255,0.2)' }}>ADMIN</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['users','Nutzer'],['invites','Invite-Codes'],['limits','Limits'],['changelog','Changelog'],['tools','Tools']].map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '7px 16px', borderRadius: 9, fontSize: 13, border: `1px solid ${tab===id ? 'rgba(0,229,255,0.35)' : 'rgba(255,255,255,0.1)'}`, background: tab===id ? 'rgba(0,229,255,0.1)' : 'transparent', color: tab===id ? '#00e5ff' : 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>{l}</button>
          ))}
          <button onClick={() => { sessionStorage.removeItem('admin_secret'); setAuthed(false); }} style={{ padding: '7px 14px', borderRadius: 9, fontSize: 13, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>Abmelden</button>
        </div>
      </div>

      <div style={{ padding: '28px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
          {[['Nutzer gesamt', totalUsers, '#00e5ff'],['Aktiv', activeUsers, '#00ff88'],['Deaktiviert', totalUsers - activeUsers, '#ff4444'],['Freie Codes', availInvites, '#ffaa00']].map(([t,v,c]) => (
            <div key={t} style={{ padding: '18px 20px', background: 'rgba(10,10,25,0.8)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{t}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: c, fontFamily: 'Arial Black' }}>{v}</div>
            </div>
          ))}
        </div>

        {msg && <div style={{ padding: '10px 16px', background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 9, marginBottom: 20, fontSize: 13, color: '#00e5ff' }}>{msg}</div>}
        {loading && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Lade...</p>}

        {/* ── NUTZER ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Alle Nutzer</h2>
              <button onClick={loadData} style={{ padding: '6px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 12 }}>↻ Aktualisieren</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {users.map(user => (
                <div key={user.id} style={{ padding: '18px 20px', background: user.active === false ? 'rgba(255,40,40,0.04)' : 'rgba(10,10,25,0.8)', border: `1px solid ${user.active === false ? 'rgba(255,40,40,0.2)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: `linear-gradient(135deg, ${TIER_COLORS[user.tier]||'#00e5ff'}, #0044ff)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#000', flexShrink: 0 }}>
                        {(user.name || user.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{user.name || '—'}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{user.email}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                          <TierBadge tier={user.tier} />
                          <StatusBadge active={user.active} />
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{user.post_count} Posts</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      <button onClick={() => setEditUser(user)} style={{ padding: '6px 12px', background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', borderRadius: 7, color: '#00e5ff', cursor: 'pointer', fontSize: 11 }}>🏢 Firmenprofil</button>
                      <button onClick={() => setLimitUser(user)} style={{ padding: '6px 12px', background: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.25)', borderRadius: 7, color: '#ffaa00', cursor: 'pointer', fontSize: 11 }}>⚙ Konto</button>
                      <button onClick={() => triggerWeekly(user.id)} style={{ padding: '6px 12px', background: 'rgba(153,68,255,0.1)', border: '1px solid rgba(153,68,255,0.3)', borderRadius: 7, color: '#aa66ff', cursor: 'pointer', fontSize: 11 }}>✦ KI-Ideen</button>
                      <button onClick={() => toggleActive(user)} style={{ padding: '6px 12px', background: user.active === false ? 'rgba(0,255,136,0.08)' : 'rgba(255,40,40,0.08)', border: `1px solid ${user.active === false ? 'rgba(0,255,136,0.25)' : 'rgba(255,40,40,0.25)'}`, borderRadius: 7, color: user.active === false ? '#00ff88' : '#ff4444', cursor: 'pointer', fontSize: 11 }}>
                        {user.active === false ? '✓ Aktivieren' : '✕ Deaktivieren'}
                      </button>
                    </div>
                  </div>
                  {user.deactivation_reason && <div style={{ marginTop: 10, padding: '6px 10px', background: 'rgba(255,40,40,0.06)', borderRadius: 6, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Grund: {user.deactivation_reason}</div>}
                  <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.2)' }}>ID: {user.id} · {new Date(user.created_at).toLocaleDateString('de-DE')}</div>
                </div>
              ))}
              {users.length === 0 && !loading && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '40px 0' }}>Keine Nutzer.</p>}
            </div>
          </div>
        )}

        {/* ── INVITE-CODES ── */}
        {tab === 'invites' && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Invite-Codes</h2>
            <div style={{ padding: 22, background: 'rgba(10,10,25,0.8)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 12, marginBottom: 22 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#00e5ff' }}>Neuen Code erstellen</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>TIER</span>
                  <select value={invTier} onChange={e => { setInvTier(e.target.value); setInvMaxUses(TIER_OPTIONS.find(t => t.value === e.target.value)?.users || 1); }} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                    {TIER_OPTIONS.map(t => <option key={t.value} value={t.value} style={{ background: '#0a0a1f' }}>{t.label} ({t.users} Nutzer)</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>MAX. NUTZER</span>
                  <input type="number" min={1} max={50} value={invMaxUses} onChange={e => setInvMaxUses(parseInt(e.target.value) || 1)} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>LABEL</span>
                  <input type="text" value={invLabel} onChange={e => setInvLabel(e.target.value)} placeholder="z.B. four7even" style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                </label>
              </div>
              <button onClick={generateInvite} style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #00e5ff, #0077ff)', border: 'none', borderRadius: 9, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Code generieren</button>
              {newInvite && (
                <div style={{ marginTop: 14, padding: '12px 16px', background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.25)', borderRadius: 9 }}>
                  <div style={{ fontSize: 22, fontFamily: 'monospace', fontWeight: 700, color: '#00ff88', letterSpacing: '0.1em', marginBottom: 6 }}>{newInvite.code}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{newInvite.tier?.toUpperCase()} · max. {newInvite.max_uses} Nutzer</div>
                  <button onClick={() => navigator.clipboard.writeText(newInvite.code)} style={{ marginTop: 8, padding: '4px 12px', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.25)', borderRadius: 6, color: '#00ff88', cursor: 'pointer', fontSize: 11 }}>Kopieren</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {invites.map(inv => {
                const used = inv.current_uses || 0; const max = inv.max_uses || 1; const full = used >= max;
                return (
                  <div key={inv.code} style={{ padding: '14px 18px', background: full ? 'rgba(255,255,255,0.02)' : 'rgba(10,10,25,0.8)', border: `1px solid ${full ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: full ? 'rgba(255,255,255,0.3)' : '#fff', letterSpacing: '0.05em' }}>{inv.code}</span>
                        <TierBadge tier={inv.tier} />
                        {inv.label && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{inv.label}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, color: full ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)' }}>{used}/{max} Nutzer</div>
                          <div style={{ width: 80, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden', marginTop: 3 }}>
                            <div style={{ height: '100%', width: `${(used/max)*100}%`, background: full ? '#666' : TIER_COLORS[inv.tier] || '#00e5ff', borderRadius: 2 }} />
                          </div>
                        </div>
                        <button onClick={() => deleteInvite(inv.code)} style={{ padding: '5px 10px', background: 'rgba(255,40,40,0.08)', border: '1px solid rgba(255,40,40,0.2)', borderRadius: 6, color: '#ff4444', cursor: 'pointer', fontSize: 11 }}>✕</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── LIMITS ── */}
        {tab === 'limits' && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Wochenlimits pro Tier</h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 20 }}>Standard-Limits je Tier. Individuelle Abweichungen: Nutzer → ⚙ Konto.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {TIER_OPTIONS.map(t => {
                const current = tierLimits.find(tl => tl.tier === t.value);
                return <TierLimitRow key={t.value} tier={t.value} label={t.label} current={current} color={TIER_COLORS[t.value]} onSave={saveTierLimit} />;
              })}
            </div>
            {tierLimits.length === 0 && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 16 }}>Tier-Limits noch nicht in der DB — schema_limits.sql ausführen.</p>}
          </div>
        )}

        {/* ── CHANGELOG ── */}
        {tab === 'changelog' && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Changelog</h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 20 }}>Neuigkeiten für den Monatsbericht und die Kunden.</p>

            {/* Formular */}
            <div style={{ padding: 22, background: 'rgba(10,10,25,0.8)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: 12, marginBottom: 22 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#00e5ff' }}>Neuen Eintrag erstellen</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>TITEL *</span>
                  <input type="text" value={clTitle} onChange={e => setClTitle(e.target.value)} placeholder="z.B. Neue Bildgenerierung mit Flux Pro"
                    style={{ padding: '9px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13 }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>BESCHREIBUNG (optional)</span>
                  <textarea value={clDesc} onChange={e => setClDesc(e.target.value)} rows={2} placeholder="Kurze Beschreibung für den Monatsbericht..."
                    style={{ padding: '9px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13, resize: 'vertical', outline: 'none' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em' }}>KATEGORIE</span>
                  <select value={clCat} onChange={e => setClCat(e.target.value)}
                    style={{ padding: '9px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' }}>
                    <option value="feature"     style={{ background: '#0a0a1f' }}>Neu — neues Feature</option>
                    <option value="improvement" style={{ background: '#0a0a1f' }}>Verbesserung</option>
                    <option value="fix"         style={{ background: '#0a0a1f' }}>Fix — Bugfix</option>
                    <option value="news"        style={{ background: '#0a0a1f' }}>Info — allgemeine Neuigkeit</option>
                  </select>
                </label>
              </div>
              <button onClick={addChangelog} disabled={clSaving || !clTitle.trim()}
                style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #00e5ff, #0077ff)', border: 'none', borderRadius: 9, color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: !clTitle.trim() ? 0.5 : 1 }}>
                {clSaving ? 'Speichere...' : '+ Eintrag speichern'}
              </button>
            </div>

            {/* Einträge */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {changelog.map(entry => {
                const catColors = { feature: '#00e5ff', fix: '#ff6b6b', improvement: '#00ff88', news: '#ffaa00' };
                const catLabels = { feature: 'Neu', fix: 'Fix', improvement: 'Verbesserung', news: 'Info' };
                const col = catColors[entry.category] || '#00e5ff';
                const lbl = catLabels[entry.category] || entry.category;
                return (
                  <div key={entry.id} style={{ padding: '14px 18px', background: 'rgba(10,10,25,0.8)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                        <span style={{ flexShrink: 0, marginTop: 2, display: 'inline-block', background: `${col}18`, border: `1px solid ${col}40`, borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, color: col, letterSpacing: '0.06em' }}>{lbl}</span>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', lineHeight: 1.4 }}>{entry.title}</div>
                          {entry.description && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 3, lineHeight: 1.5 }}>{entry.description}</div>}
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', marginTop: 5 }}>{new Date(entry.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                        </div>
                      </div>
                      <button onClick={() => deleteChangelog(entry.id)} style={{ padding: '4px 10px', background: 'rgba(255,40,40,0.08)', border: '1px solid rgba(255,40,40,0.2)', borderRadius: 6, color: '#ff4444', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>✕</button>
                    </div>
                  </div>
                );
              })}
              {changelog.length === 0 && <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '40px 0' }}>Noch keine Einträge.</p>}
            </div>
          </div>
        )}

        {/* ── TOOLS ── */}
        {tab === 'tools' && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>Admin-Tools</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <ToolCard title="KI-Ideen für alle generieren" color="#9944ff" desc="Wöchentliche Content-Ideen für alle aktiven Nutzer." action="Für alle generieren"
                onAction={async () => { const d = await adminApi('/weekly-suggestions/trigger', { method: 'POST', body: JSON.stringify({}) }); showMsg(d.message); }} />
              <ToolCard title="Nutzerliste aktualisieren" color="#00e5ff" desc="Lädt alle Nutzerdaten neu." action="Aktualisieren" onAction={loadData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TierLimitRow({ tier, label, current, color, onSave }) {
  const [posts,     setPosts]     = useState(current?.weekly_posts     ?? '');
  const [publishes, setPublishes] = useState(current?.weekly_publishes ?? '');
  const [images,    setImages]    = useState(current?.weekly_images    ?? '');
  const [saving,    setSaving]    = useState(false);

  useEffect(() => {
    if (current) { setPosts(current.weekly_posts); setPublishes(current.weekly_publishes); setImages(current.weekly_images); }
  }, [current]);

  async function handleSave() { setSaving(true); await onSave(tier, posts, publishes, images); setSaving(false); }

  return (
    <div style={{ padding: '18px 20px', background: 'rgba(10,10,25,0.8)', border: `1px solid ${color}20`, borderRadius: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ width: 4, height: 28, borderRadius: 2, background: color, flexShrink: 0 }} />
        <TierBadge tier={tier} />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{label}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr) auto', gap: 12, alignItems: 'flex-end' }}>
        {[['Posts/Woche', posts, setPosts], ['Publishes/Woche', publishes, setPublishes], ['Bilder/Woche', images, setImages]].map(([lbl, val, setter]) => (
          <label key={lbl} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>{lbl.toUpperCase()}</span>
            <input type="number" min={0} max={9999} value={val} onChange={e => setter(e.target.value)}
              style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#fff', fontSize: 14, textAlign: 'center' }} />
          </label>
        ))}
        <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 8, color, cursor: 'pointer', fontSize: 12, fontWeight: 600, height: 40 }}>
          {saving ? '...' : '▶ Speichern'}
        </button>
      </div>
    </div>
  );
}

function TierBadge({ tier }) {
  const c = TIER_COLORS[tier] || '#888';
  return <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: `${c}18`, color: c, border: `1px solid ${c}30`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tier || 'basic'}</span>;
}

function StatusBadge({ active }) {
  return <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: active === false ? 'rgba(255,40,40,0.1)' : 'rgba(0,255,136,0.1)', color: active === false ? '#ff4444' : '#00ff88', border: `1px solid ${active === false ? 'rgba(255,40,40,0.25)' : 'rgba(0,255,136,0.25)'}` }}>{active === false ? '✕ Deaktiviert' : '✓ Aktiv'}</span>;
}

function ToolCard({ title, desc, color, action, onAction }) {
  const [running, setRunning] = useState(false);
  async function handle() { setRunning(true); try { await onAction(); } catch {} setRunning(false); }
  return (
    <div style={{ padding: 22, background: 'rgba(10,10,25,0.8)', border: `1px solid ${color}20`, borderRadius: 12 }}>
      <div style={{ height: 3, background: color, borderRadius: 2, marginBottom: 14, width: 40 }} />
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#fff' }}>{title}</h3>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, marginBottom: 16 }}>{desc}</p>
      <button onClick={handle} disabled={running} style={{ padding: '8px 18px', background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 8, color, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{running ? 'Läuft...' : action}</button>
    </div>
  );
}
